import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Button, Checkbox } from "@mui/material";
import dayjs from "dayjs";
import DateTimeModal from "../Schedule/ScheduleListAdd/DateTimeModal";
import { TbFilterEdit } from "react-icons/tb";
import AddBucketModal from "../../components/Buckets/AddEditBucket";
import { RxPlus } from "react-icons/rx";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import DeleteModal from "../../components/Model/DeleteModal";

function Bucket() {
  const [search, setSearch] = useState("");
  const [buckets, setBuckets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [rowCount, setRowCount] = useState(0);
  const [editData, setEditData] = useState(null);
  const [openDateModal, setOpenDateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const dateFilterData = JSON.parse(localStorage.getItem("bucketDateFilter"));
  const [startDate, setStartDate] = useState(
    dateFilterData?.startDate || dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    dateFilterData?.endDate || dayjs().endOf("month").format("YYYY-MM-DD")
  );
  const [selectedRadio, setSelectedRadio] = useState(
    dateFilterData?.selectedRadio || "month"
  );
  const [page, setPage] = useState(0);

  // selection state like clone
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedId, setSelectedId] = useState([]);
  const [allIds, setAllIds] = useState(0);

  const httpService = new HttpService();
  const [loading, setLoading] = useState(false);

  const fetchBuckets = async () => {
    setLoading(true);
    try {
      const res = await httpService.get(
        `bucket?start_date=${startDate}&end_date=${endDate}&search=${encodeURIComponent(
          search || ""
        )}&page=${page + 1}&pageSize=${pageSize}`
      );
      const list = res?.data?.data || res?.data?.data || [];
      const mapped = Array.isArray(list)
        ? list.map((row) => ({
            id: row.id,
            name: row.room_name, // backend returns room_id only; replace with room name if join added
            epicenter: row.epicenter,
            bucketId: row.bucket_id,
            occupied: row.is_occupied,
          }))
        : [];
      setRowCount(res?.data?.pagination?.total);
      setBuckets(mapped);
    } catch (e) {
      setBuckets([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBuckets();
  }, [search, startDate, endDate, page, pageSize, selectedRadio]);

  const handleDeleteMultiple = async () => {
    if (!selectedId || selectedId.length === 0) return;
    setLoading(true);
    try {
      const res = await httpService.post("bucket/deleteMultiple", {
        ids: selectedId,
      });
      if (res?.success) {
        toast.success(res?.message || "Deleted successfully");
        setSelectedId([]);
        setIsAllSelected(false);
        setAllIds(0);
        fetchBuckets();
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (e) {
      toast.error("Delete failed.");
    }
    setLoading(false);
    setOpenDeleteModal(false);
  };

  const handleOpenDeletePrompt = () => {
    if (!selectedId || selectedId.length === 0) {
      toast.error("Please select at least one record to delete");
      return;
    }
    setOpenDeleteModal(true);
  };

  const isIdSelected = (id) => {
    return selectedId?.includes(id);
  };

  const toggleId = (id) => {
    setSelectedId((prev = []) => {
      if (prev.includes(id)) {
        const updated = prev.filter((item) => item !== id);
        if (updated.length < allIds) {
          setIsAllSelected(false);
        }
        return updated;
      } else {
        const updated = [...prev, id];
        if (updated.length === allIds) {
          setIsAllSelected(true);
        }
        return updated;
      }
    });
  };

  const handleHeaderCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const ids = buckets?.map((row) => row.id) || [];
      setSelectedId(ids);
      setAllIds(ids.length);
    } else {
      setSelectedId([]);
      setAllIds(0);
    }
    setIsAllSelected(isChecked);
  };

  const handleEdit = (bucket) => {
    setEditData(bucket);
    setShowModal(true);
  };

  const columns = [
    {
      field: "select_All",
      headerName: "Select All",
      minWidth: 150,
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Checkbox
            value="Checked"
            checked={
              isAllSelected ? true : isIdSelected(params.row.id) ? true : false
            }
            onChange={() => toggleId(params.row.id)}
          />
        );
      },
      renderHeader: () => {
        return (
          <>
            <Checkbox
              checked={isAllSelected}
              onChange={(e) => handleHeaderCheckboxChange(e)}
            />
            <strong>Select All</strong>
          </>
        );
      },
    },
    { field: "name", headerName: "Room Name", flex: 1, minWidth: 150 },
    { field: "epicenter", headerName: "Epicenter", flex: 1, minWidth: 120 },
    { field: "bucketId", headerName: "Bucket ID", flex: 1, minWidth: 150 },
    {
      field: "occupied",
      headerName: "Occupied",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    // action column kept commented per request
  ];

  return (
    <div className="clone">
      <div className="page-title">
        <div className="title-wrap">
          <h1>Bucket ID</h1>
        </div>
        <div className="button-wrap">
          <button
            type="submit"
            className="btn btn-primary"
            onClick={() => {
              setShowModal(true);
            }}
          >
            <RxPlus /> Add Bucket ID
          </button>
        </div>
      </div>

      <div className="table-filter-wrap d-flex justify-content-between pb-0">
        <div className="header_search-wrap">
          <div className="header_search_bar common_content d-flex">
            <div className="input-group input_group">
              <button className="btn search-btn search_btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  <circle
                    cx="9.98856"
                    cy="9.98856"
                    r="8.98856"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.2402 16.7071L19.7643 20.222"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                type="search"
                className="form-control form_control"
                placeholder="Search..."
                aria-label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        {/* <Button
          variant="outlined"
          color="primary"
          onClick={() => setOpenDateModal(true)}
          className="filters-btn"
          style={{ textTransform: "none" }}
        >
          <TbFilterEdit /> Filters
        </Button> */}
      </div>
      <div className="pt-2">
        <Button className="btn btn-delete" onClick={handleOpenDeletePrompt}>
          Delete
        </Button>
      </div>

      <div style={{ width: "100%", marginTop: 24 }}>
        <DataGrid
          rows={buckets}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          pageSizeOptions={[15, 50, 100]}
          paginationModel={{ pageSize, page }}
          paginationMode="server"
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          getRowHeight={() => "auto"}
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 0.5 },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
        />
      </div>

      {openDateModal && (
        <DateTimeModal
          handleDateTimeModal={() => setOpenDateModal(false)}
          setStartDate={(e) => setStartDate(e)}
          setEndDate={(e) => setEndDate(e)}
          open={openDateModal}
          fetchSchedule={() => {
            const bucketDateFilter = {
              startDate: startDate,
              endDate: endDate,
              selectedRadio: selectedRadio,
            };
            localStorage.setItem(
              "bucketDateFilter",
              JSON.stringify(bucketDateFilter)
            );
            setOpenDateModal(false);
          }}
          selectedRadio={selectedRadio}
          setSelectedRadio={(e) => setSelectedRadio(e)}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      <AddBucketModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditData(null);
        }}
        editData={editData}
        fetchBuckets={fetchBuckets}
      />
      {openDeleteModal && (
        <DeleteModal
          open={openDeleteModal}
          HandleClose={() => setOpenDeleteModal(false)}
          HandleDelete={handleDeleteMultiple}
        />
      )}
    </div>
  );
}

export default Bucket;
